import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLazyGetMQLQuery, useLazyGetPersonasQuery, useUpdateFeedbackMutation } from "../../services/mqlAPI";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  TextField,
  Button,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  IconButton,
  Collapse
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import styled from 'styled-components'
import MUIDataTable from 'mui-datatables';

const TableDiv = styled.div`
  margin-top: 20px;
  margin-right: 2px;
  margin-left: 2px;
  `

export type Mqls_list = {
  mql_id: string;
  mql_company_name: string;
  mql_industry: string;
  predict_score: number;
  number_of_persona: number;
}

export type Personas = {
  idx: number;
  persona: string;
  job: string;
  issue: string;
  needs: string;
  proposition: string;
  feedback_info: { 
    feedback: number;
    detail: string;
  };
};

const PersonaPage = () => {
  const location = useLocation();
  const [minute_id, setMinute_id] = useState(new URLSearchParams(location.search).get("minute_id") || "");
  const [issue_id, setIssueId] = useState(new URLSearchParams(location.search).get("id") || "");
  const [getMQL, { data:mqlData, error:mqlError, isLoading:mqlLoading }] = useLazyGetMQLQuery();
  const [updateFeedback, { data:feedbackData, error:feedbackError, isLoading:feedbackLoading }] = useUpdateFeedbackMutation();
  const [reLoading, setReLoading] = useState(false); // 検索中の状態を管理するための状態
  const [popupOpen, setPopupOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Login属性情報
  // const { user, signOut } = useAuthenticator((context) => [context.user]);

  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [personasData, setPersonasData] = useState<{ [key: number]: Personas[] }>({});
  const [getPersonas] = useLazyGetPersonasQuery();

  const transformedmqlData: Mqls_list[] = Array.isArray(mqlData) ? mqlData : [];

  useEffect(() => {
    const fetchData = async () => {
      const MQLData = {
        minute_id: minute_id,
        issue_id: issue_id
      }
      try {
        setReLoading(true);
        const response = await getMQL(MQLData);
        setReLoading(false)
        if (response.error) {
          const error = response.error as any;
          if (error.status === "FETCH_ERROR") {
            setPopupOpen(true);
            setErrorMessage("サーバータイムアウト");
          } else {
            setPopupOpen(true);
            setErrorMessage("サーバーエラー");
          }
        } 
      } catch (error) {
        setPopupOpen(true);
        setErrorMessage("予期せぬエラーが発生しました");
        setReLoading(false)
        console.log('予期せぬエラーが発生しました:', error);
      }
    };

    if (issue_id) {
      fetchData();
    }
  }, [issue_id, getMQL, feedbackData]);


  const handleRowClick = async (rowIndex: number, updataFlag: number) => {
    const mql_id = transformedmqlData[rowIndex].mql_id;
    if (expandedRows.includes(rowIndex) && updataFlag !== 1) {
      setExpandedRows(prev => prev.filter(index => index !== rowIndex));
    } else {
      if (issue_id && mql_id) {
        const { data } = await getPersonas({ minute_id: minute_id, issue_id: issue_id, mql_id: mql_id });
        const transformedpersonasData: Personas[] = Array.isArray(data) ? data : [];
        if (data){
          setPersonasData(prev => ({ ...prev, [rowIndex]: transformedpersonasData }));
          setExpandedRows(prev => [...prev, rowIndex]);
        }
      }
    }
  };

  const columns = [
    {
      name: 'mql_id',
      label: 'ID',
    },
    {
      name: "mql_company_name",
      label: "見込み顧客",
      options: { filter: true },
    },
    {
      name: "mql_industry",
      label: "業界",
      options: { filter: true },
    },
    {
      name: "predict_score",
      label: "スコア",
      options: { filter: true },
    }
  ]

  const options = {
    // 高さ指定
    tableBodyHeight: "650px",
  
    // 列選択のcheckboxを表示しない
    selectableRows: 'none' as any,
    filter: false,
    search: true,
    print: false,
    download: false,
    viewColumns: false,
  
    rowsPerPage: 100,
    rowsPerPageOptions: [],
    textLabels: {
      body: {
        noMatch: `表示できるデータが存在しません`,
      }
    },
    customRowRender: (data: any[], dataIndex: number, rowIndex: number) => {
      return (
        <>
          <TableRow>
            <TableCell>
              <IconButton onClick={() => handleRowClick(rowIndex, 0)}>
                {expandedRows.includes(rowIndex) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
              <span>{rowIndex+1}</span>
            </TableCell>
            {data.map((cell, index) => (
              <TableCell key={index}>{cell}</TableCell>
            ))}
          </TableRow>
          {expandedRows.includes(rowIndex) && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Collapse in={expandedRows.includes(rowIndex)} timeout="auto" unmountOnExit>
                  <Box margin={1}>
                    {personasData[rowIndex] && <ExpandedTable personas={personasData[rowIndex]!} mql_id={transformedmqlData[rowIndex].mql_id} rowIndex={rowIndex} onButtonClick={handleButtonClick}/>}
                    </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          )}
        </>
      );
    }
  }

  //エラー対策
  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop limit exceeded') {
        e.stopImmediatePropagation();
      }
    };
  
    window.addEventListener('error', handleResizeObserverError);
  
    return () => {
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, []);

  const handleButtonClick = (idx: number, feedback: number, detail: string, mql_id: string, rowIndex: number) => {
    const feedbackData = {
      minute_id: minute_id,
      issue_id: issue_id,
      mql_id: mql_id,
      idx: String(idx),
      feedback: feedback,
      detail: detail
    }
    const fetchUpdateFeedback = async () => {
      try {
        const response = await updateFeedback(feedbackData);
        if ('error' in response) {
          console.log('error in response:',response)
          if ('status' in response.error) {
            switch (response.error.status) {
              case 400:
                setPopupOpen(true);
                setErrorMessage("エラーが発生しました。議事録が未記入か容量をオーバーしています。");
                console.error('400エラー:',response)
                break;
              default:
                setPopupOpen(true);
                setErrorMessage("エラーが発生しました。");
                console.error('エラーが発生しました。:',response)
            }
          }
        } 
      }catch (error) {
        setPopupOpen(true);
        setErrorMessage("予期せぬエラーが発生しました。");
        console.error('予期せぬエラーが発生しました:', error);
      }
    }

    fetchUpdateFeedback()
    handleRowClick(rowIndex, 1)
  }


  const ExpandedTable = ({ personas, mql_id, rowIndex, onButtonClick }: { personas: Personas[], mql_id: string, rowIndex: number, onButtonClick: (idx: number, feedback: number, detail: string, mql_id: string, rowIndex: number) => void }) => {
    const [feedbackValues, setFeedbackValues] = useState<{ [key: number]: number }>({});
    const [detailValues, setDetailValues] = useState<{ [key: number]: string }>({});

    useEffect(() => {
      const initialFeedbackValues = personas.reduce((acc, persona) => {
        acc[persona.idx] = persona.feedback_info.feedback;
        return acc;
      }, {} as { [key: number]: number });
      setFeedbackValues(initialFeedbackValues);

      const initialDetailValues = personas.reduce((acc, persona) => {
        acc[persona.idx] = persona.feedback_info.detail;
        return acc;
      }, {} as { [key: number]: string });
      setDetailValues(initialDetailValues);
    }, [personas]);

    const handleFeedbackChange = (idx: number, value: number) => {
      setFeedbackValues(prev => ({ ...prev, [idx]: value }));
    };
  
    const handleDetailChange = (idx: number, value: string) => {
      setDetailValues(prev => ({ ...prev, [idx]: value }));
    };  

    return (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>No.</TableCell>
                <TableCell>ペルソナ</TableCell>
                <TableCell>業務内容</TableCell>
                <TableCell>課題</TableCell>
                <TableCell>ニーズ</TableCell>
                <TableCell>価値提案</TableCell>
                <TableCell>ユーザー評価</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {personas.map((persona, index) => {
                return (
                <TableRow key={persona.idx}>
                  <TableCell>{persona.idx}</TableCell>
                  <TableCell>{persona.persona}</TableCell>
                  <TableCell>{persona.job}</TableCell>
                  <TableCell>{persona.issue}</TableCell>
                  <TableCell>{persona.needs}</TableCell>
                  <TableCell>{persona.proposition}</TableCell>
                  <TableCell>
                  <Select
                    defaultValue={persona.feedback_info.feedback}
                    value={feedbackValues[persona.idx]}
                    onChange={(e) => handleFeedbackChange(persona.idx, e.target.value as number)}
                    size="small"
                    sx={{ fontSize: '0.875rem',width: '100%' }}
                  >
                    <MenuItem value={1}>良い</MenuItem>
                    <MenuItem value={0}>どちらでもない</MenuItem>
                    <MenuItem value={-1}>悪い</MenuItem>
                  </Select>
                  <TextField
                    value={detailValues[persona.idx]}
                    onChange={(e) => handleDetailChange(persona.idx, e.target.value)}
                    variant="outlined"
                    size="small"
                    style={{ fontSize: '0.5rem', marginTop: '8px' }}
                    multiline
                    rows={3}
                  />
                  </TableCell>
                  <Button
                    variant="contained"
                    size="small"
                    style={{ fontSize: '0.875rem', marginTop: '8px' }}
                    onClick={() => onButtonClick(persona.idx, feedbackValues[persona.idx], detailValues[persona.idx], mql_id, rowIndex)}
                  >
                    送信
                  </Button>
                </TableRow>
              )})}
            </TableBody>
          </Table>
    )
  };

  return (
    <div>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
        {/* 上の行 */}
        <h3>ペルソナ推定画面</h3>
        {/* 下の行 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 5, display: 'flex', justifyContent: 'center' }}>
            <TextField
              variant="filled"
              label="既存顧客課題"
              value={new URLSearchParams(location.search).get("issue") || "課題が選択されていません"}
              multiline
              InputProps={{
                readOnly: true,
              }}
              sx={{ width: '80%' }}
            />
          </Box> 
        </Box>
      </Box>
      {(mqlLoading || reLoading) && (
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      )}
      {!mqlLoading && !mqlData && <p>データがありません</p>}
      {transformedmqlData ? (

        <TableDiv>
          {transformedmqlData && (
            <div>
            <MUIDataTable
              title=""
              data={transformedmqlData.map(mql => [
                mql.mql_company_name,
                mql.mql_industry,
                mql.predict_score,
              ])}
              columns={columns}
              options={options}
            />
            </div>
          )}
        </TableDiv>
      ) : (
        <div>Loading...</div>
      )}
      <Snackbar
        open={popupOpen}
        autoHideDuration={5000}
        onClose={() => setPopupOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // 画面中央に表示
      >
        <Alert onClose={() => setPopupOpen(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PersonaPage;